import {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  useMemo,
  ReactNode,
} from 'react';
import { SnackBar } from '~/components/commons/modals/Snackbar';

const SnackbarContext = createContext<Snackbar | null>(null);

interface Snackbar {
  open: (type: string, message: any, options: any) => void;
  close: () => void;
  alert: (message: any, options?: any) => void;
  success: (message: any, options?: any) => void;
}

export const SnackbarProvider = ({ children }: { children: ReactNode }) => {
  const [snackPack, setSnackPack] = useState<
    { message: string; key: number }[]
  >([]);
  const [messageInfo, setMessageInfo] = useState<{
    message: string;
    key: number;
  } | null>(null);
  const [show, setShow] = useState(false);
  const [type, setType] = useState<'unoblue' | 'alert'>('unoblue');
  const [options, setOptions] = useState<any>({});

  const alert = useCallback((msg, options?) => {
    setType('alert');
    setSnackPack((prev) => [
      ...prev,
      { message: msg, key: new Date().getTime() },
    ]);
    setOptions(options);
  }, []);

  const success = useCallback((msg, options?) => {
    setType('unoblue');
    setSnackPack((prev) => [
      ...prev,
      { message: msg, key: new Date().getTime() },
    ]);
    setOptions(options);
  }, []);

  const open = useCallback((type, msg, options) => {
    setType(type);
    setSnackPack((prev) => [
      ...prev,
      { message: msg, key: new Date().getTime() },
    ]);
    setOptions(options);
  }, []);

  const close = useCallback(() => {
    setShow(false);
    setMessageInfo(null);
  }, []);

  useEffect(() => {
    if (snackPack.length && !messageInfo) {
      setMessageInfo({ ...snackPack[0] });
      setSnackPack((prev) => prev.slice(1));
      setShow(true);
    } else if (snackPack.length && messageInfo && show) {
      setShow(false);
      setMessageInfo(null);
    }
  }, [snackPack, messageInfo, show]);

  const value = useMemo(
    () => ({
      open,
      close,
      alert,
      success,
    }),
    [alert, close, open, success]
  );

  return (
    <SnackbarContext.Provider value={value}>
      {children}
      {show && (
        <SnackBar
          color={type}
          show={show}
          message={messageInfo ? messageInfo.message : ''}
          onClosed={close}
          leadingItems={options?.leadingItems}
          actionItems={options?.actionItems}
        />
      )}
    </SnackbarContext.Provider>
  );
};

export function useSnackbarContext() {
  return useContext(SnackbarContext) as Snackbar;
}
